import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Target, Save, AlertCircle, Trash2, ExternalLink, Sparkles, Copy, Check,
  Wand2, ListChecks, Link2, Loader2, Zap, CheckCircle2, XCircle,
} from "lucide-react";
import {
  adminGetGoogleAds, adminPutGoogleAds, adminDeleteGoogleAds, adminGenerateAdCopy,
  getGoogleAdsPublic,
} from "../../lib/api";
import { trackAdsConversion } from "../../lib/googleAds";

const CANONICAL = "https://torontocustomtshirts.ca";

/* --------------------------- small helpers --------------------------- */
function CopyButton({ text, testid, label = "Copy" }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  return (
    <button
      type="button" onClick={copy} data-testid={testid}
      className="inline-flex items-center gap-1.5 border border-[#333] hover:border-white text-zinc-300 hover:text-white px-2.5 py-1 font-mono text-[10px] tracking-widest"
    >
      {done ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
      {done ? "COPIED" : label}
    </button>
  );
}

function CopyList({ title, items, testid }) {
  if (!items?.length) return null;
  return (
    <div className="border border-[#222] bg-black p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[10px] tracking-widest text-zinc-400">{title}</div>
        <CopyButton text={items.join("\n")} testid={`${testid}-copy-all`} label="Copy all" />
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-3 group">
            <span className="font-mono text-xs text-zinc-200 break-words">{it}</span>
            <span className="font-mono text-[9px] text-zinc-600 shrink-0">{it.length}c</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


/* Fires a REAL Google Ads conversion client-side so the operator can verify
   tracking end-to-end in seconds (via Google Tag Assistant) instead of
   waiting hours for Google Ads to report. Each ping is tagged with a
   TEST-<ts> transaction id and a $1.23 nominal value. */
const TEST_GOALS = [
  { key: "purchase", l: "Purchase" },
  { key: "quote", l: "Quote Request" },
  { key: "add_to_cart", l: "Add to Cart" },
  { key: "begin_checkout", l: "Begin Checkout" },
];

function TestConversion() {
  const [labels, setLabels] = useState(null);
  const [goal, setGoal] = useState("purchase");
  const [firing, setFiring] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getGoogleAdsPublic().then((c) => setLabels(c?.labels || {})).catch(() => setLabels({}));
  }, []);

  const fire = async () => {
    setResult(null);
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      setResult({ ok: false, msg: "gtag isn't loaded on this page — is the Google tag present in index.html?" });
      return;
    }
    if (!labels?.[goal]) {
      setResult({ ok: false, msg: `No "${goal}" label configured — add it above and Save first.` });
      return;
    }
    setFiring(true);
    const dl = window.dataLayer || [];
    const before = dl.length;
    const txn = `TEST-${Date.now()}`;
    try {
      await trackAdsConversion({ labelKey: goal, value: 1.23, currency: "CAD", transactionId: txn });
      // Give gtag a tick to push onto dataLayer. gtag pushes an
      // `arguments` object (array-like, NOT a real Array), so we can't use
      // Array.isArray — check the shape instead.
      await new Promise((r) => setTimeout(r, 500));
      const landed = (window.dataLayer || []).slice(before).some(
        (e) => e && e[1] === "conversion"
      );
      setResult(landed
        ? { ok: true, msg: `Conversion fired ✓ (send_to: AW-18264990337/${labels[goal]}, txn ${txn}). Check Google Tag Assistant to confirm Google received it.` }
        : { ok: false, msg: "trackAdsConversion ran but no conversion event landed in dataLayer — check the browser console." });
    } catch (e) {
      setResult({ ok: false, msg: String(e?.message || e) });
    } finally { setFiring(false); }
  };

  return (
    <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-4" data-testid="ads-test-conversion">
      <div className="flex items-center gap-2">
        <Zap size={16} className="text-[var(--tc-red)]" />
        <h3 className="font-display text-lg uppercase tracking-tight">Test a conversion</h3>
      </div>
      <p className="text-xs text-zinc-400 leading-relaxed">
        Fire a real test conversion right now to confirm tracking works — no need to wait hours or place a real order.
        It sends a $1.23 ping tagged <code className="text-zinc-300">TEST-…</code>. Watch it land live in
        {" "}<a href="https://tagassistant.google.com" target="_blank" rel="noreferrer" className="text-[var(--tc-red)] hover:underline inline-flex items-center gap-1">Google Tag Assistant <ExternalLink size={11} /></a>.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="ads-test-goal-select"
          className="bg-black border border-[#333] px-3 py-2 font-mono text-xs text-zinc-100 focus:border-[var(--tc-red)] focus:outline-none">
          {TEST_GOALS.map((g) => (
            <option key={g.key} value={g.key} disabled={labels && !labels[g.key]}>
              {g.l}{labels && !labels[g.key] ? " (no label)" : ""}
            </option>
          ))}
        </select>
        <button type="button" onClick={fire} disabled={firing} data-testid="ads-fire-test-btn"
          className="bg-white text-black px-4 py-2 font-mono text-xs tracking-widest hover:bg-zinc-200 disabled:opacity-50 flex items-center gap-2">
          {firing ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
          {firing ? "FIRING…" : "FIRE TEST CONVERSION"}
        </button>
      </div>
      {result && (
        <div data-testid="ads-test-result" className={`flex items-start gap-2 border p-3 text-xs ${result.ok ? "border-emerald-700 bg-emerald-950/30 text-emerald-200" : "border-red-800 bg-red-950/30 text-red-200"}`}>
          {result.ok ? <CheckCircle2 size={15} className="mt-0.5 shrink-0" /> : <XCircle size={15} className="mt-0.5 shrink-0" />}
          <span>{result.msg}</span>
        </div>
      )}
      <p className="font-mono text-[10px] text-zinc-500">
        Tip: to watch it live, open Tag Assistant → &quot;Add domain&quot; → your site URL → connect, then click FIRE. You&apos;ll see the AW conversion event appear instantly. Test hits may show in Google Ads within a few hours — you can ignore/exclude the $1.23 TEST rows.
      </p>
    </div>
  );
}

/* ===================================================================== */
/* Section 1 — Conversion Tracking                                        */
/* ===================================================================== */
function ConversionTracking({ pw }) {
  const [form, setForm] = useState({
    conversion_id: "", label_purchase: "", label_quote: "",
    label_add_to_cart: "", label_begin_checkout: "", enabled: true,
  });
  const [savedId, setSavedId] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetGoogleAds(pw).then((r) => {
      setForm({
        conversion_id: r.conversion_id || "",
        label_purchase: r.label_purchase || "",
        label_quote: r.label_quote || "",
        label_add_to_cart: r.label_add_to_cart || "",
        label_begin_checkout: r.label_begin_checkout || "",
        enabled: r.enabled !== false,
      });
      setSavedId(r.conversion_id || "");
      setUpdatedAt(r.updated_at);
    }).catch(() => {});
  }, [pw]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e?.preventDefault?.();
    if (form.conversion_id && !/^AW-\d{6,15}$/.test(form.conversion_id.trim())) {
      toast.error("Conversion ID must look like AW-1234567890");
      return;
    }
    setSaving(true);
    try {
      const r = await adminPutGoogleAds(pw, form);
      setSavedId(r.conversion_id || "");
      setUpdatedAt(r.updated_at);
      toast.success("Google Ads tracking saved — live on the site now (no redeploy).");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const disable = async () => {
    if (!window.confirm("Disable Google Ads tracking? Conversions will stop firing.")) return;
    try {
      await adminDeleteGoogleAds(pw);
      setForm({ conversion_id: "", label_purchase: "", label_quote: "", label_add_to_cart: "", label_begin_checkout: "", enabled: true });
      setSavedId("");
      toast.success("Google Ads tracking disabled");
    } catch (e) { toast.error("Failed to disable"); }
  };

  const labelFields = [
    { k: "label_purchase", l: "PURCHASE (checkout success)", req: true },
    { k: "label_quote", l: "QUOTE REQUEST (lead)", req: true },
    { k: "label_add_to_cart", l: "ADD TO CART (optional)", req: false },
    { k: "label_begin_checkout", l: "BEGIN CHECKOUT (optional)", req: false },
  ];

  return (
    <div className="space-y-6" data-testid="ads-conversion-tracking">
      <div className={`border p-4 ${savedId ? "border-emerald-700 bg-emerald-950/30" : "border-amber-700 bg-amber-950/30"}`}>
        <div className="flex items-start gap-2">
          <AlertCircle size={16} className={savedId ? "text-emerald-400 mt-0.5" : "text-amber-400 mt-0.5"} />
          <div className="flex-1">
            <div className={`font-mono text-xs tracking-widest ${savedId ? "text-emerald-200" : "text-amber-200"}`} data-testid="ads-tracking-status">
              STATUS: {savedId ? "GOOGLE ADS TRACKING IS LIVE" : "NOT CONFIGURED YET"}
            </div>
            {savedId ? (
              <div className="text-xs text-zinc-400 mt-1">
                Conversion ID <code className="text-zinc-200">{savedId}</code>
                {!form.label_purchase && !form.label_quote && (
                  <span className="text-amber-300"> · add at least one conversion label below to record conversions</span>
                )}
                {updatedAt && <> · saved {new Date(updatedAt).toLocaleString()}</>}
              </div>
            ) : (
              <div className="text-xs text-zinc-400 mt-1">Paste your Conversion ID + labels below to start recording conversions.</div>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={save} className="space-y-4 border border-[#222] bg-[#0a0a0a] p-5">
        <label className="block">
          <span className="font-mono text-[10px] tracking-widest text-zinc-400">GOOGLE ADS CONVERSION ID (AW-XXXXXXXXX)</span>
          <input
            type="text" value={form.conversion_id} onChange={set("conversion_id")}
            data-testid="ads-conversion-id-input" placeholder="AW-18264990337"
            className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none"
          />
        </label>

        <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-[#222]">
          {labelFields.map(({ k, l, req }) => (
            <label key={k} className="block">
              <span className="font-mono text-[10px] tracking-widest text-zinc-400">
                {l} {req && <span className="text-[var(--tc-red)]">*</span>}
              </span>
              <input
                type="text" value={form[k]} onChange={set(k)}
                data-testid={`ads-${k.replace("label_", "")}-label-input`}
                placeholder="e.g. abcDEF123gHI"
                className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-xs text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none"
              />
            </label>
          ))}
        </div>
        <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
          The label is the string AFTER the slash in your conversion snippet
          (<code className="text-zinc-400">send_to: 'AW-…/<b>THIS_PART</b>'</code>).
          You can paste the whole <code className="text-zinc-400">AW-…/label</code> too — we&apos;ll extract it.
        </p>

        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving} data-testid="ads-save-btn"
            className="bg-[var(--tc-red)] text-white px-5 py-2 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
            <Save size={14} /> {saving ? "SAVING…" : "SAVE TRACKING"}
          </button>
          {savedId && (
            <button type="button" onClick={disable} data-testid="ads-disable-btn"
              className="border border-red-900 hover:bg-red-950 text-red-300 px-4 py-2 font-mono text-xs tracking-widest flex items-center gap-2">
              <Trash2 size={14} /> DISABLE
            </button>
          )}
        </div>
      </form>

      <TestConversion />

      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3">
        <h3 className="font-display text-lg uppercase tracking-tight">How to get your labels (5 min)</h3>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-zinc-300">
          <li>Open <a href="https://ads.google.com/aw/conversions" target="_blank" rel="noreferrer" className="text-[var(--tc-red)] hover:underline inline-flex items-center gap-1">Google Ads → Goals → Conversions <ExternalLink size={12} /></a>.</li>
          <li>Click <strong>+ New conversion action → Website</strong>. Create one named <strong>Purchase</strong> (category: Purchase) and one named <strong>Quote Request</strong> (category: Submit lead form).</li>
          <li>Choose <strong>&quot;Use Google tag&quot;</strong> setup (the tag is already installed on your site). Google shows a snippet with <code className="text-zinc-400">send_to: 'AW-18264990337/xxxx'</code> — copy the part after the slash into the matching field above.</li>
          <li>Enable <strong>Enhanced conversions</strong> in each action — this site already sends hashed email/phone for ~30% cheaper clicks.</li>
        </ol>
      </div>
    </div>
  );
}

/* ===================================================================== */
/* Section 2 — AI Ad Copy Generator                                       */
/* ===================================================================== */
function AdCopyGenerator({ pw }) {
  const [subject, setSubject] = useState("");
  const [audience, setAudience] = useState("");
  const [landing, setLanding] = useState("");
  const [tone, setTone] = useState("confident, local, benefit-driven");
  const [promo, setPromo] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (subject.trim().length < 2) { toast.error("Tell me what you're advertising"); return; }
    setLoading(true); setResult(null);
    try {
      const r = await adminGenerateAdCopy(pw, {
        subject: subject.trim(), audience: audience.trim(),
        landing_url: landing.trim(), tone: tone.trim(), promo: promo.trim(),
      });
      setResult(r);
      toast.success("Ad copy ready — copy it into your Google Ads RSA.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed — try again");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="ads-copy-generator">
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-4">
        <label className="block">
          <span className="font-mono text-[10px] tracking-widest text-zinc-400">WHAT ARE YOU ADVERTISING? *</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="adcopy-subject-input"
            placeholder="e.g. Custom team t-shirts for sports clubs"
            className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none" />
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">TARGET AUDIENCE</span>
            <input value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="adcopy-audience-input"
              placeholder="e.g. Sports teams & schools in the GTA"
              className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-xs text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">LANDING PAGE URL</span>
            <input value={landing} onChange={(e) => setLanding(e.target.value)} data-testid="adcopy-landing-input"
              placeholder={`${CANONICAL}/custom`}
              className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-xs text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">TONE</span>
            <input value={tone} onChange={(e) => setTone(e.target.value)} data-testid="adcopy-tone-input"
              className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-xs text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">PROMO / OFFER (optional)</span>
            <input value={promo} onChange={(e) => setPromo(e.target.value)} data-testid="adcopy-promo-input"
              placeholder="e.g. 10% off first order with WELCOME10"
              className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-xs text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none" />
          </label>
        </div>
        <button onClick={generate} disabled={loading} data-testid="adcopy-generate-btn"
          className="bg-[var(--tc-red)] text-white px-5 py-2 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {loading ? "WRITING…" : "GENERATE AD COPY"}
        </button>
        <p className="font-mono text-[10px] text-zinc-500">Powered by Claude Sonnet 5. Every headline ≤ 30 chars, description ≤ 90 chars — paste straight into a Responsive Search Ad.</p>
      </div>

      {result && (
        <div className="space-y-4" data-testid="adcopy-results">
          <CopyList title={`HEADLINES (${result.headlines.length}) — max 30 chars`} items={result.headlines} testid="adcopy-headlines" />
          <CopyList title={`DESCRIPTIONS (${result.descriptions.length}) — max 90 chars`} items={result.descriptions} testid="adcopy-descriptions" />
          <div className="grid sm:grid-cols-2 gap-4">
            <CopyList title="KEYWORDS" items={result.keywords} testid="adcopy-keywords" />
            <CopyList title="NEGATIVE KEYWORDS" items={result.negative_keywords} testid="adcopy-negatives" />
            <CopyList title="SITELINKS" items={result.sitelinks} testid="adcopy-sitelinks" />
            <CopyList title="CALLOUTS" items={result.callouts} testid="adcopy-callouts" />
          </div>
        </div>
      )}
    </div>
  );
}

/* ===================================================================== */
/* Section 3 — Ad Kit (pre-built, static)                                 */
/* ===================================================================== */
const KIT_KEYWORDS = [
  '"custom t shirts mississauga"', '"custom t shirt printing toronto"',
  '"design your own t shirt"', '"custom shirts near me"',
  '"dtf transfers toronto"', '"dtf printing near me"',
  '"bulk t shirt printing toronto"', '"custom hoodies toronto"',
  '"team jerseys custom mississauga"', '"dtf printing toronto"',
  '[custom apparel gta]', '[custom t shirts brampton]',
];
const KIT_NEGATIVES = [
  "free", "cheap", "jobs", "salary", "how to", "diy", "tutorial",
  "iron on", "wholesale blank", "used", "second hand", "near me jobs",
];
const KIT_CALLOUTS = ["No Minimums", "Free Mockups", "Local Pickup", "Bulk Discounts", "Fast Turnaround", "Printed in Mississauga"];
const KIT_SITELINKS = ["Design Online", "DTF Transfers", "Bulk Quote", "Ready-Made Tees"];

function AdKit() {
  return (
    <div className="space-y-4" data-testid="ads-kit">
      <div className="border border-emerald-900 bg-emerald-950/20 p-4">
        <p className="text-sm text-zinc-300">Battle-tested starter lists for a GTA custom-apparel Search campaign. Copy them straight into Google Ads. Split keywords into tight ad groups (T-Shirts / DTF / Bulk) for the best Quality Score.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <CopyList title="STARTER KEYWORDS" items={KIT_KEYWORDS} testid="kit-keywords" />
        <CopyList title="NEGATIVE KEYWORDS (add campaign-wide)" items={KIT_NEGATIVES} testid="kit-negatives" />
        <CopyList title="CALLOUT EXTENSIONS" items={KIT_CALLOUTS} testid="kit-callouts" />
        <CopyList title="SITELINK LABELS" items={KIT_SITELINKS} testid="kit-sitelinks" />
      </div>
    </div>
  );
}

/* ===================================================================== */
/* Section 4 — UTM Builder                                                */
/* ===================================================================== */
function UtmBuilder() {
  const [base, setBase] = useState(`${CANONICAL}/custom`);
  const [source, setSource] = useState("google");
  const [medium, setMedium] = useState("cpc");
  const [campaign, setCampaign] = useState("");
  const [term, setTerm] = useState("");
  const [content, setContent] = useState("");

  const build = () => {
    if (!base) return "";
    try {
      const url = new URL(base);
      const add = (k, v) => v && url.searchParams.set(k, v.trim().replace(/\s+/g, "_").toLowerCase());
      add("utm_source", source); add("utm_medium", medium); add("utm_campaign", campaign);
      add("utm_term", term); add("utm_content", content);
      return url.toString();
    } catch { return "Invalid base URL"; }
  };
  const built = build();

  const F = ({ label, val, on, ph, testid }) => (
    <label className="block">
      <span className="font-mono text-[10px] tracking-widest text-zinc-400">{label}</span>
      <input value={val} onChange={(e) => on(e.target.value)} placeholder={ph} data-testid={testid}
        className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-xs text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none" />
    </label>
  );

  return (
    <div className="space-y-4" data-testid="ads-utm-builder">
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-4">
        <F label="LANDING URL" val={base} on={setBase} ph={CANONICAL} testid="utm-base-input" />
        <div className="grid sm:grid-cols-3 gap-4">
          <F label="SOURCE" val={source} on={setSource} ph="google" testid="utm-source-input" />
          <F label="MEDIUM" val={medium} on={setMedium} ph="cpc" testid="utm-medium-input" />
          <F label="CAMPAIGN" val={campaign} on={setCampaign} ph="spring_tees" testid="utm-campaign-input" />
          <F label="TERM (keyword)" val={term} on={setTerm} ph="custom t shirts" testid="utm-term-input" />
          <F label="CONTENT (ad variant)" val={content} on={setContent} ph="rsa_a" testid="utm-content-input" />
        </div>
      </div>
      <div className="border border-emerald-900 bg-emerald-950/20 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono text-[10px] tracking-widest text-emerald-300">TRACKING URL</div>
          <CopyButton text={built} testid="utm-copy-btn" />
        </div>
        <div className="font-mono text-xs text-emerald-200 break-all" data-testid="utm-result">{built}</div>
      </div>
      <p className="font-mono text-[10px] text-zinc-500">Paste this as your ad&apos;s Final URL (or use the campaign field in Google Ads) so UTM data shows up in GA4 under Acquisition.</p>
    </div>
  );
}

/* ===================================================================== */
/* Parent                                                                 */
/* ===================================================================== */
const SECTIONS = [
  { k: "tracking", l: "Conversion Tracking", Icon: Target },
  { k: "copy", l: "AI Ad Copy", Icon: Sparkles },
  { k: "kit", l: "Ad Kit", Icon: ListChecks },
  { k: "utm", l: "UTM Builder", Icon: Link2 },
];

export default function AdsTab({ pw }) {
  const [sec, setSec] = useState("tracking");
  return (
    <div className="space-y-6" data-testid="ads-tab">
      <div>
        <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
          <Target size={22} className="text-[var(--tc-red)]" /> Google Ads
        </h2>
        <p className="font-body text-sm text-zinc-400 mt-1">
          Plug in conversion tracking (no redeploy), generate high-converting ad copy with AI, grab a ready-made keyword kit, and build UTM links.
        </p>
      </div>

      <div className="flex gap-1.5 flex-wrap border-b border-[#222] pb-2">
        {SECTIONS.map(({ k, l, Icon }) => (
          <button key={k} onClick={() => setSec(k)} data-testid={`ads-sec-${k}`}
            className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5 ${
              sec === k ? "bg-[var(--tc-red)] border-[var(--tc-red)] text-white" : "border-[#222] text-zinc-400 hover:text-white"
            }`}>
            <Icon size={11} /> {l}
          </button>
        ))}
      </div>

      {sec === "tracking" && <ConversionTracking pw={pw} />}
      {sec === "copy" && <AdCopyGenerator pw={pw} />}
      {sec === "kit" && <AdKit />}
      {sec === "utm" && <UtmBuilder />}
    </div>
  );
}
